import * as React from 'react';
import { AlertTriangle, Check, Info, X } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { WorkspaceButton, useToast } from '@/components/ui/workspace';
import { WorkspaceInput } from '@/components/ui/workspace/field';
import { useFirmAiBudgetQuery } from '@/lib/api/ai-costs';
import {
  errorMessage,
  reviewCount,
  useAddName,
  useKbImports,
  useKbNames,
  useKbReview,
  useKbSettings,
  useNameAction,
  useNameException,
  usePutSettings,
  type KbImportRun,
  type KbNames,
  type KbRescan,
} from '@/lib/api/vendor-knowledge';
import { cn } from '@/lib/utils/cn';
import {
  Chip,
  DChip,
  FormError,
  Linkish,
  MonoNo,
  Page,
  PageHead,
  Tip,
  smallControl,
} from '../kb-bits';
import { fmt, formatDate, money, plural, reviewAgeText, shortDate } from '../kb-logic';
import { ReasonForm } from '../review/flags';
import { useKb } from '../vendor-knowledge-screen';

/**
 * Knowledge base settings (prototype `settingsView()`; T-0207, E13-F01 §3,
 * E13-F03 §3, E13-F08): the protected names every write is checked
 * against, how often pages are checked and when items are due for review,
 * the upload links' lifetime and the AI budget (both read-only here, set
 * elsewhere in Firm settings), and the history of the Caliber v1 import.
 * Curators only.
 */
export function KbSettingsPage() {
  return (
    <Page>
      <PageHead title="Knowledge base settings">
        Rules for the whole firm. They apply to every vendor and partner.
      </PageHead>
      <ProtectedNames />
      <Checking />
      <LinksAndBudget />
      <Imports />
    </Page>
  );
}

function Card({ id, title, children }: { id?: string; title: string; children: React.ReactNode }) {
  return (
    <section
      id={id}
      aria-label={title}
      className="mt-4 rounded-card border border-ink-100 bg-paper-0 px-6 py-5 first-of-type:mt-6"
    >
      <h2 className="m-0 mb-1.5 text-[15px] font-semibold leading-[22px] text-ink-900">{title}</h2>
      {children}
    </section>
  );
}

function SetRow({
  title,
  hint,
  control,
}: {
  title: string;
  hint: React.ReactNode;
  control: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center gap-6 border-t border-ink-100 py-3.5 first-of-type:mt-2 sm:flex-nowrap">
      <div className="min-w-0 flex-1">
        <div className="font-medium text-ink-900">{title}</div>
        <div className="text-meta text-ink-500">{hint}</div>
      </div>
      <div className="flex-none">{control}</div>
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[11px] font-medium uppercase leading-[18px] tracking-[0.06em] text-ink-500">
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Protected names
// ---------------------------------------------------------------------------

function rescanLine(name: string, r: KbRescan): { tone: 'ok' | 'warn'; text: string } {
  const hits = r.redacted_proposals + r.flagged_items + r.flagged_citations;
  return hits > 0
    ? {
        tone: 'warn',
        text: `Added “${name}”. Checked ${plural(r.scanned, 'item')}: ${plural(hits, 'item')} had the name and ${hits === 1 ? 'waits' : 'wait'} under its vendor’s To review.`,
      }
    : { tone: 'ok', text: `Added “${name}”. Checked ${plural(r.scanned, 'item')}. None had it.` };
}

function ProtectedNames() {
  const names = useKbNames();
  return (
    <Card id="kb-set-names" title="Protected names">
      <p className="m-0 max-w-[760px] text-body-sm text-ink-700">
        Caliber never stores a client’s name in the knowledge base. Every import, AI proposal and
        edit is checked against these names on the server. A match becomes “[client name removed]”
        and waits in To review until a curator has looked at it.
      </p>
      {names.isPending ? (
        <div className="mt-4">
          <Spinner />
        </div>
      ) : names.isError ? (
        <FormError>{errorMessage(names.error)}</FormError>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-1 gap-7 lg:grid-cols-2">
            <FromClients data={names.data} />
            <FromCurators data={names.data} />
          </div>
          {names.data.import.length > 0 ? <FromImport data={names.data} /> : null}
        </>
      )}
    </Card>
  );
}

const listClass = 'm-0 mb-3 mt-2.5 list-none border-t border-ink-100 p-0';
const rowClass =
  'flex min-h-[38px] flex-wrap items-center gap-1.5 border-b border-ink-100 py-1.5 text-body-sm';

function FromClients({ data }: { data: KbNames }) {
  const { toast } = useToast();
  const exception = useNameException();
  const [marking, setMarking] = React.useState<{ client: string; text: string } | null>(null);
  return (
    <div>
      <Label>From client records (automatic)</Label>
      <div className="text-meta text-ink-500">
        Every client in Caliber, with the short forms code derives. Kept up to date from the client
        records. A short form that is an ordinary word can be marked “Not a name”.
      </div>
      {data.from_clients.length === 0 ? (
        <div className="mt-2.5 text-meta text-ink-500">No clients yet.</div>
      ) : (
        <ul className={listClass}>
          {data.from_clients.map((c) => (
            <li key={c.client_id} className={rowClass}>
              <span className="mr-1 text-ink-900">{c.name}</span>
              {c.short_forms.map((f) =>
                f.exception ? (
                  <span key={f.text} className="inline-flex items-center gap-1">
                    <DChip title="Marked “Not a name”: not protected">
                      <span className="text-ink-300 line-through">{f.text}</span>
                    </DChip>
                    {f.exception_id ? (
                      <Linkish
                        tone="quiet"
                        onClick={() =>
                          exception.mutate(
                            { retire: f.exception_id! },
                            {
                              onSuccess: () =>
                                toast({ message: `“${f.text}” is protected again.` }),
                              onError: (err) =>
                                toast({ message: errorMessage(err), tone: 'critical' }),
                            },
                          )
                        }
                      >
                        Protect again
                      </Linkish>
                    ) : null}
                  </span>
                ) : (
                  <span key={f.text} className="inline-flex items-center gap-1">
                    <DChip>{f.text}</DChip>
                    <Linkish
                      tone="quiet"
                      onClick={() => setMarking({ client: c.client_id, text: f.text })}
                    >
                      Not a name
                    </Linkish>
                  </span>
                ),
              )}
            </li>
          ))}
        </ul>
      )}
      {marking ? (
        <ReasonForm
          label={`Why is “${marking.text}” not a name?`}
          placeholder="For example: it is an ordinary word in vendor documents"
          help="It is no longer removed from new text. Text already removed stays removed."
          button="Not a name"
          pending={exception.isPending}
          onCancel={() => setMarking(null)}
          onSubmit={(reason) =>
            exception.mutate(
              { add: { client_id: marking.client, text: marking.text, reason } },
              {
                onSuccess: () => {
                  toast({ message: `“${marking.text}” is no longer treated as a name.` });
                  setMarking(null);
                },
                onError: (err) => toast({ message: errorMessage(err), tone: 'critical' }),
              },
            )
          }
        />
      ) : null}
    </div>
  );
}

function FromCurators({ data }: { data: KbNames }) {
  const { toast } = useToast();
  const add = useAddName();
  const action = useNameAction();
  const [name, setName] = React.useState('');
  const [result, setResult] = React.useState<{ tone: 'ok' | 'warn'; text: string } | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [removing, setRemoving] = React.useState<string | null>(null);
  const live = data.curator.filter((n) => n.status === 'active');

  const submit = () => {
    const clean = name.trim().replace(/\s+/g, ' ');
    if (!clean) return;
    setError(null);
    add.mutate(
      { name: clean },
      {
        onSuccess: (out) => {
          setResult(rescanLine(clean, out.rescan));
          setName('');
        },
        onError: (err) => setError(errorMessage(err)),
      },
    );
  };

  return (
    <div>
      <Label>Added by curators</Label>
      <div className="text-meta text-ink-500">
        Names the client records don’t have, such as Caliber v1’s clients and other short forms.
      </div>
      <ul className={listClass}>
        {live.map((n) => (
          <li key={n.id} className={rowClass}>
            <span className="min-w-0 flex-1 text-ink-900">{n.name}</span>
            <span className="text-meta text-ink-500">{shortDate(n.added_at)}</span>
            <button
              type="button"
              aria-label={`Remove ${n.name}`}
              title="Remove. Text already removed stays removed."
              onClick={() => setRemoving(n.id)}
              className="inline-flex h-[26px] w-[26px] items-center justify-center rounded-md text-ink-500 hover:bg-ink-100 hover:text-ink-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent-amber-600"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </li>
        ))}
        {live.length === 0 ? <li className={cn(rowClass, 'text-ink-500')}>None yet.</li> : null}
      </ul>
      {removing ? (
        <ReasonForm
          label="Why stop protecting this name?"
          help="Text already removed stays removed."
          button="Remove"
          destructive
          pending={action.isPending}
          onCancel={() => setRemoving(null)}
          onSubmit={(reason) =>
            action.mutate(
              { id: removing, verb: 'retire', reason },
              {
                onSuccess: () => {
                  toast({
                    message: 'Removed from protected names. Text already removed stays removed.',
                  });
                  setRemoving(null);
                },
                onError: (err) => toast({ message: errorMessage(err), tone: 'critical' }),
              },
            )
          }
        />
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        <WorkspaceInput
          className="min-w-[200px] flex-1"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit();
          }}
          placeholder="A client name or short form"
          aria-label="Name to protect"
          autoComplete="off"
        />
        <WorkspaceButton size="md" disabled={!name.trim() || add.isPending} onClick={submit}>
          Add
        </WorkspaceButton>
      </div>
      {error ? <FormError>{error}</FormError> : null}
      {result ? (
        <div
          role="status"
          className={cn(
            'mt-2.5 flex items-start gap-2 rounded-card px-3 py-2.5 text-body-sm text-ink-900',
            result.tone === 'ok' ? 'bg-posture-healthy-tint' : 'bg-posture-at-risk-tint',
          )}
        >
          {result.tone === 'ok' ? (
            <Check
              className="mt-[3px] h-3.5 w-3.5 flex-none text-posture-healthy"
              aria-hidden="true"
            />
          ) : (
            <AlertTriangle
              className="mt-[3px] h-3.5 w-3.5 flex-none text-posture-at-risk"
              aria-hidden="true"
            />
          )}
          <span>{result.text}</span>
        </div>
      ) : null}
    </div>
  );
}

function FromImport({ data }: { data: KbNames }) {
  const { toast } = useToast();
  const action = useNameAction();
  const [rejecting, setRejecting] = React.useState<string | null>(null);
  const pending = data.import.filter((n) => n.status === 'pending');
  const decided = data.import.filter((n) => n.status !== 'pending');
  return (
    <div className="mt-6" id="kb-import-names">
      <Label>From the Caliber v1 import</Label>
      <div className="text-meta text-ink-500">
        Names found in Caliber v1’s project list. They mix client names with ordinary words, so each
        waits for a curator: approve a client name, reject an ordinary word.
      </div>
      {pending.length > 0 ? (
        <ul className={listClass}>
          {pending.map((n) => (
            <li key={n.id} className={rowClass}>
              <span className="min-w-0 flex-1 text-ink-900">{n.name}</span>
              <Chip tone="amber">Waiting</Chip>
              <WorkspaceButton
                disabled={action.isPending}
                onClick={() =>
                  action.mutate(
                    { id: n.id, verb: 'approve' },
                    {
                      onSuccess: (out) =>
                        toast({
                          message: out.rescan
                            ? rescanLine(n.name, out.rescan).text.replace('Added', 'Protected')
                            : `Protected “${n.name}”.`,
                        }),
                      onError: (err) => toast({ message: errorMessage(err), tone: 'critical' }),
                    },
                  )
                }
              >
                Approve
              </WorkspaceButton>
              <WorkspaceButton variant="tertiary" onClick={() => setRejecting(n.id)}>
                Reject
              </WorkspaceButton>
            </li>
          ))}
        </ul>
      ) : (
        <div className="mt-2.5 text-meta text-ink-500">Every name from the import is decided.</div>
      )}
      {rejecting ? (
        <ReasonForm
          label="Why is it not a client name?"
          placeholder="For example: an ordinary word"
          button="Reject"
          pending={action.isPending}
          onCancel={() => setRejecting(null)}
          onSubmit={(reason) =>
            action.mutate(
              { id: rejecting, verb: 'reject', reason },
              {
                onSuccess: () => {
                  toast({ message: 'Rejected. It is not protected.' });
                  setRejecting(null);
                },
                onError: (err) => toast({ message: errorMessage(err), tone: 'critical' }),
              },
            )
          }
        />
      ) : null}
      {decided.length > 0 ? (
        <details className="mt-1 text-meta text-ink-500">
          <summary className="cursor-pointer">Decided names</summary>
          <ul className="m-0 mt-1.5 flex list-none flex-wrap gap-1.5 p-0">
            {decided.map((n) => (
              <li key={n.id}>
                <DChip title={n.status === 'active' ? 'Protected' : 'Not protected'}>
                  <span className={n.status === 'active' ? '' : 'text-ink-300 line-through'}>
                    {n.name}
                  </span>
                </DChip>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Checking web pages
// ---------------------------------------------------------------------------

const REFRESH = [30, 60, 90, 180];
const REVIEW_AGE = [180, 365, 730, 1095];

function Checking() {
  const { toast } = useToast();
  const settings = useKbSettings();
  const review = useKbReview(null, true);
  const put = usePutSettings();
  const due = reviewCount(review.data, 'due_for_review');
  if (!settings.data) return null;
  const s = settings.data;
  const save = (body: { refresh_days?: number; review_after_days?: number }, message: string) =>
    put.mutate(body, {
      onSuccess: () => toast({ message }),
      onError: (err) => toast({ message: errorMessage(err), tone: 'critical' }),
    });
  return (
    <Card title="Checking web pages">
      <SetRow
        title="Check each page every"
        hint="Unchanged pages cost nothing. A changed page has only its changed passages read again. One page can have its own interval, in its details under Sources."
        control={
          <select
            className={`${smallControl} pr-7`}
            aria-label="Check each page every"
            value={s.refresh_days}
            disabled={put.isPending}
            onChange={(e) =>
              save(
                { refresh_days: Number(e.target.value) },
                `Pages are checked every ${e.target.value} days, unless a page has its own interval.`,
              )
            }
          >
            {[...new Set([...REFRESH, s.refresh_days])]
              .sort((a, b) => a - b)
              .map((d) => (
                <option key={d} value={d}>
                  {d} days
                </option>
              ))}
          </select>
        }
      />
      <SetRow
        title="Mark items for review when they are older than"
        hint={
          review.data
            ? due > 0
              ? `${plural(due, 'item')} ${due === 1 ? 'is' : 'are'} older than this today.`
              : 'No item is older than this today.'
            : ''
        }
        control={
          <select
            className={`${smallControl} pr-7`}
            aria-label="Mark items for review when they are older than"
            value={s.review_after_days}
            disabled={put.isPending}
            onChange={(e) =>
              save(
                { review_after_days: Number(e.target.value) },
                `Items older than ${reviewAgeText(Number(e.target.value))} are listed for review.`,
              )
            }
          >
            {[...new Set([...REVIEW_AGE, s.review_after_days])]
              .sort((a, b) => a - b)
              .map((d) => (
                <option key={d} value={d}>
                  {reviewAgeText(d)}
                </option>
              ))}
          </select>
        }
      />
      <Tip icon={<Info className="h-3.5 w-3.5" />}>
        Searching for new pages happens only when a curator asks. Fetching reaches only the public
        internet, respects robots.txt, and has size and type limits.
      </Tip>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Upload links and the AI budget: read here, set elsewhere
// ---------------------------------------------------------------------------

function LinksAndBudget() {
  const { openPath } = useKb();
  const settings = useKbSettings();
  const budget = useFirmAiBudgetQuery();
  return (
    <>
      <Card title="Vendor upload links">
        <SetRow
          title={
            settings.data
              ? `A new link works for ${plural(settings.data.link_ttl_days, 'day')}`
              : 'A new link works for the firm’s secure-link lifetime'
          }
          hint="The firm’s secure-link setting, shared with Discovery’s client links. Each link is for one vendor and one request, shows nothing else, can be revoked, and holds files until a curator accepts them."
          control={null}
        />
      </Card>
      <Card title="AI budget">
        <SetRow
          title={
            budget.data
              ? `${money(budget.data.remaining_cents)} left this month`
              : 'The firm’s monthly AI budget'
          }
          hint={
            budget.data
              ? `Of the firm’s ${money(budget.data.cap_cents)}. Reading pages and documents and searching use it. From the AI spend ledger.`
              : 'Reading pages and documents and searching use it. It is set and watched in Firm settings.'
          }
          control={
            <WorkspaceButton onClick={() => openPath('/settings/ai-costs')}>
              AI costs
            </WorkspaceButton>
          }
        />
      </Card>
    </>
  );
}

// ---------------------------------------------------------------------------
// Imports
// ---------------------------------------------------------------------------

function num(report: Record<string, unknown>, key: string): number {
  const v = report[key];
  return typeof v === 'number' ? v : 0;
}

function ImportRun({ run }: { run: KbImportRun }) {
  const r = run.report;
  const byKind = (r['items_by_kind'] ?? {}) as Record<string, number>;
  const answers = byKind['requirement_response'] ?? 0;
  const items =
    Object.values(byKind).reduce((a, b) => a + (typeof b === 'number' ? b : 0), 0) - answers;
  const notImported = Object.keys((r['fields_not_imported'] ?? {}) as Record<string, number>);
  const rows: Array<[string, string]> =
    run.stage === 'names'
      ? [
          [
            'Names found',
            `${fmt(num(r, 'candidates_found'))} in ${plural(num(r, 'projects_read'), 'project')}`,
          ],
          ['Waiting for a decision', fmt(num(r, 'names_added'))],
          [
            'Already protected or listed',
            fmt(num(r, 'names_already_protected') + num(r, 'names_already_listed')),
          ],
        ]
      : [
          [
            'Catalog',
            `${plural(num(r, 'vendors'), 'vendor')}, ${plural(num(r, 'products'), 'product')} and ${plural(num(r, 'partners'), 'implementation partner')}`,
          ],
          ['Items', plural(items, 'item')],
          [
            'Requirement answers',
            `${fmt(answers)}${num(r, 'answer_rows_read') ? `, from ${fmt(num(r, 'answer_rows_read'))} rows read` : ''}`,
          ],
          ['Web pages registered', fmt(num(r, 'pages_registered'))],
          ['Client names removed', fmt(num(r, 'names_removed'))],
          [
            'Not imported',
            notImported.length
              ? 'v1 project names, and the ratings and scores v1 produced without a source'
              : 'Nothing left out',
          ],
        ];
  return (
    <div className="border-t border-ink-100 py-3.5">
      <div className="mb-2 flex flex-wrap items-baseline gap-3">
        <span className="font-semibold text-ink-900">
          {formatDate(run.finished_at ?? run.started_at)}
        </span>
        <Chip
          tone={
            run.status === 'completed'
              ? 'healthy'
              : run.status === 'failed'
                ? 'critical'
                : 'neutral'
          }
        >
          {run.stage === 'names' ? 'Names' : 'Records'} ·{' '}
          {run.status === 'completed' ? 'done' : run.status.replace(/_/g, ' ')}
        </Chip>
        <MonoNo className="text-[11px] text-ink-500">
          sha256 {run.file_checksum.slice(0, 12)}…
        </MonoNo>
        <span className="flex-1" />
        <span className="text-meta text-ink-500">Run by {run.actor.name}</span>
      </div>
      <dl className="m-0 grid grid-cols-1 gap-x-4 gap-y-1.5 text-body-sm sm:grid-cols-[180px_minmax(0,1fr)]">
        {rows.map(([k, v]) => (
          <React.Fragment key={k}>
            <dt className="text-meta text-ink-500">{k}</dt>
            <dd className="m-0 text-ink-900">{v}</dd>
          </React.Fragment>
        ))}
      </dl>
    </div>
  );
}

function Imports() {
  const imports = useKbImports();
  const runs = imports.data?.runs ?? [];
  return (
    <Card id="kb-set-imports" title="Imports">
      <p className="m-0 max-w-[760px] text-body-sm text-ink-700">
        Caliber v1’s vendor knowledge base, imported by a script run against its export file.
        Everything it brought in is cited as Avero Advisors Vendor Knowledge Base.
      </p>
      {imports.isPending ? (
        <div className="mt-3">
          <Spinner />
        </div>
      ) : runs.length === 0 ? (
        <div className="mt-3 text-meta text-ink-500">Not imported yet.</div>
      ) : (
        <div className="mt-3">
          {runs.map((r) => (
            <ImportRun key={r.id} run={r} />
          ))}
        </div>
      )}
      <Tip icon={<Info className="h-3.5 w-3.5" />}>
        Running it again adds nothing already imported.
      </Tip>
    </Card>
  );
}
